import {
  BedDouble,
  Clock,
  Drumstick,
  FileText,
  Gift,
  History,
  LayoutGrid,
  Package,
  Percent,
  Printer,
  ScrollText,
  Settings as SettingsIcon,
  ShieldCheck,
  ShoppingCart,
  Sparkles,
  Tag,
  Users as UsersIcon,
  type LucideIcon,
} from 'lucide-react';

/**
 * THE ROUTE / NAVIGATION REGISTRY: single source of truth for the sidebar, Alt+N shortcuts and routes.
 * Order here = sidebar order (mirrors the original desktop app).
 *
 * HOW A SCREEN AGENT PLUGS IN (no edit to this file needed):
 *   create `src/features/<screen>/index.tsx` with a default-exported page component.
 *   The entry below whose `screen` equals `<screen>` automatically renders it instead of the placeholder.
 *   Sub-pages: the entry path is mounted as `<path>/*`, so use nested <Routes> inside your feature.
 *
 * Need an extra top-level route that is not in the sidebar (e.g. a printable receipt page)?
 * Add ONE line to `extraRoutes` below.
 *
 * Do not rename `id`/`path`/`screen` values: other screens link to them (`paths.pos` etc.).
 */

export type Audience = 'all' | 'admin' | 'super_admin';
export type FeatureFlag = 'rooms_enabled' | 'shifts_enabled';

export interface NavEntry {
  id: string;
  path: string;
  label: string;
  icon: LucideIcon;
  /** Who may see/open it. admin = admin + super_admin. */
  audience: Audience;
  /** Hidden (and route blocked) unless this public setting is true. */
  feature?: FeatureFlag;
  /** Folder name under src/features that provides the page. */
  screen: string;
  /** Visual divider above this item in the sidebar. */
  group: 'main' | 'admin' | 'system';
}

export const navEntries: NavEntry[] = [
  {
    id: 'pos',
    path: '/pos',
    label: 'POS',
    icon: ShoppingCart,
    audience: 'all',
    screen: 'pos',
    group: 'main',
  },
  {
    id: 'products',
    path: '/products',
    label: 'Retail Products',
    icon: Package,
    audience: 'all',
    screen: 'products',
    group: 'main',
  },
  {
    id: 'categories',
    path: '/categories',
    label: 'Category & Supplier',
    icon: Tag,
    audience: 'all',
    screen: 'categories',
    group: 'main',
  },
  {
    id: 'tables',
    path: '/tables',
    label: 'Tables & Waiters',
    icon: LayoutGrid,
    audience: 'all',
    screen: 'tables',
    group: 'main',
  },
  {
    id: 'rooms',
    path: '/rooms',
    label: 'Rooms',
    icon: BedDouble,
    audience: 'all',
    feature: 'rooms_enabled',
    screen: 'rooms',
    group: 'main',
  },
  {
    id: 'modifiers',
    path: '/modifiers',
    label: 'Modifiers',
    icon: Sparkles,
    audience: 'all',
    screen: 'modifiers',
    group: 'main',
  },
  {
    id: 'kitchen-items',
    path: '/kitchen-items',
    label: 'Kitchen Items',
    icon: Drumstick,
    audience: 'all',
    screen: 'kitchen-items',
    group: 'main',
  },
  {
    id: 'packages',
    path: '/packages',
    label: 'Packages',
    icon: Gift,
    audience: 'all',
    screen: 'packages',
    group: 'main',
  },
  {
    id: 'promotions',
    path: '/promotions',
    label: 'Promotions',
    icon: Percent,
    audience: 'all',
    screen: 'promotions',
    group: 'main',
  },
  {
    id: 'bills',
    path: '/bills',
    label: 'Bill History',
    icon: ScrollText,
    audience: 'all',
    screen: 'bills',
    group: 'main',
  },
  {
    id: 'reports',
    path: '/reports',
    label: 'Reports',
    icon: FileText,
    audience: 'admin',
    screen: 'reports',
    group: 'admin',
  },
  {
    id: 'users',
    path: '/users',
    label: 'Users',
    icon: UsersIcon,
    audience: 'admin',
    screen: 'users',
    group: 'admin',
  },
  {
    id: 'shifts',
    path: '/shifts',
    label: 'Shifts',
    icon: Clock,
    audience: 'admin',
    feature: 'shifts_enabled',
    screen: 'shifts',
    group: 'admin',
  },
  {
    id: 'activity-log',
    path: '/activity-log',
    label: 'Activity Log',
    icon: History,
    audience: 'admin',
    screen: 'activity-log',
    group: 'admin',
  },
  {
    id: 'settings',
    path: '/settings',
    label: 'Settings',
    icon: SettingsIcon,
    // Everyone: the Printers tab is per PC (cashiers set up their till's printer); the other tabs are admin-only.
    audience: 'all',
    screen: 'settings',
    group: 'admin',
  },
  {
    id: 'print-agents',
    path: '/print-agents',
    label: 'Print Agents',
    icon: Printer,
    audience: 'admin',
    screen: 'print-agents',
    group: 'admin',
  },
  {
    id: 'admin',
    path: '/admin',
    label: 'Admin',
    icon: ShieldCheck,
    audience: 'super_admin',
    screen: 'admin',
    group: 'system',
  },
];

/** Typed path lookup: `paths.pos` -> '/pos'. Use for <Link to> and navigate(). */
export const paths = Object.fromEntries(navEntries.map((e) => [e.id, e.path])) as Record<
  | 'pos'
  | 'products'
  | 'categories'
  | 'tables'
  | 'rooms'
  | 'modifiers'
  | 'kitchen-items'
  | 'packages'
  | 'promotions'
  | 'bills'
  | 'reports'
  | 'users'
  | 'shifts'
  | 'activity-log'
  | 'settings'
  | 'print-agents'
  | 'admin',
  string
>;

export interface ExtraRoute {
  path: string;
  /** Folder under src/features providing the page (default export). */
  screen: string;
  audience: Audience;
}

/** Routes inside the app shell that are not sidebar items. ONE line per route. */
export const extraRoutes: ExtraRoute[] = [
  // { path: '/receipt/:id', screen: 'receipt', audience: 'all' },
];

/** Where "/" and post-login go. */
export const HOME_PATH = '/pos';
